import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface StudentHeaderProps {
  userProfile: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onProfileUpdate?: (profile: any) => void;
}

export default function StudentHeader({
  userProfile,
  activeTab,
  setActiveTab,
  onProfileUpdate,
}: StudentHeaderProps) {
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const handleLogout = () => {
    try {
      localStorage.removeItem("supabase_session");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("userType");
      localStorage.removeItem("userEmail");
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      // Still navigate to login even if localStorage fails
      navigate("/login");
    }
  };

  const tabs = [
    {
      id: "dashboard",
      name: "Tổng quan",
      icon: "ri-dashboard-line",
      color: "blue",
    },
    { id: "courses", name: "Khóa học", icon: "ri-book-line", color: "green" },
    {
      id: "assignments",
      name: "Bài tập",
      icon: "ri-file-list-line",
      color: "yellow",
    },
    {
      id: "schedule",
      name: "Lịch học",
      icon: "ri-calendar-line",
      color: "purple",
    },
    {
      id: "progress",
      name: "Tiến độ",
      icon: "ri-bar-chart-line",
      color: "red",
    },
  ];

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            {/* <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 hover:from-blue-600 hover:via-green-600 hover:to-yellow-600 transition-all"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Olympic Academy
              </button>
            </div> */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/favicon.png"
                  alt="Olympic Academy Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h1
                  className="text-xl font-bold text-gray-900"
                  style={{
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    fontWeight: "700",
                  }}
                >
                  Olympic Academy
                </h1>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-4 xl:space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${
                          tab.color === "blue"
                            ? "from-blue-100 to-blue-200 text-blue-700"
                            : tab.color === "green"
                            ? "from-green-100 to-green-200 text-green-700"
                            : tab.color === "yellow"
                            ? "from-yellow-100 to-yellow-200 text-yellow-700"
                            : tab.color === "purple"
                            ? "from-purple-100 to-purple-200 text-purple-700"
                            : "from-red-100 to-red-200 text-red-700"
                        }`
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-100 to-green-100 rounded-full flex items-center justify-center overflow-hidden">
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <i className="ri-user-line text-blue-600"></i>
                    )}
                  </div>
                  <div className="hidden lg:block">
                    <p className="text-sm font-medium text-gray-900">
                      {userProfile?.full_name || "Học sinh"}
                    </p>
                    <p className="text-xs text-gray-500">Học sinh</p>
                  </div>
                  <i className="ri-arrow-down-s-line text-gray-400 hidden lg:block"></i>
                </button>

                {/* Dropdown Menu */}
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setShowProfileModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-user-settings-line mr-3"></i>
                      Chỉnh sửa profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <i className="ri-logout-box-line mr-3"></i>
                      Đăng xuất
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <i
                  className={`${
                    showMobileMenu ? "ri-close-line" : "ri-menu-line"
                  } text-xl`}
                ></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden border-t border-gray-200 py-2">
            <div className="flex space-x-1 overflow-x-auto pb-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${
                          tab.color === "blue"
                            ? "from-blue-100 to-blue-200 text-blue-700"
                            : tab.color === "green"
                            ? "from-green-100 to-green-200 text-green-700"
                            : tab.color === "yellow"
                            ? "from-yellow-100 to-yellow-200 text-yellow-700"
                            : tab.color === "purple"
                            ? "from-purple-100 to-purple-200 text-purple-700"
                            : "from-red-100 to-red-200 text-red-700"
                        }`
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <i className={`${tab.icon} mr-1`}></i>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-2">
              <div className="flex items-center space-x-3 pb-3 border-b border-gray-200">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-green-100 rounded-full flex items-center justify-center overflow-hidden">
                  {userProfile?.avatar_url ? (
                    <img
                      src={userProfile.avatar_url}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <i className="ri-user-line text-blue-600"></i>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {userProfile?.full_name || "Học sinh"}
                  </p>
                  <p className="text-xs text-gray-500">Học sinh</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowProfileModal(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <i className="ri-user-settings-line mr-3"></i>
                Chỉnh sửa profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <i className="ri-logout-box-line mr-3"></i>
                Đăng xuất
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <ProfileEditModal
          userProfile={userProfile}
          onClose={() => setShowProfileModal(false)}
          onUpdate={(updatedProfile) => {
            // Update parent component
            if (onProfileUpdate) {
              onProfileUpdate(updatedProfile);
            }
            // Update localStorage
            localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
            setShowProfileModal(false);
          }}
        />
      )}
    </>
  );
}

// Profile Edit Modal Component
function ProfileEditModal({
  userProfile,
  onClose,
  onUpdate,
}: {
  userProfile: any;
  onClose: () => void;
  onUpdate: (profile: any) => void;
}) {
  const [formData, setFormData] = useState({
    full_name: userProfile?.full_name || "",
    email: userProfile?.email || "",
    phone: userProfile?.phone || "",
    bio: userProfile?.bio || "",
    avatar_url: userProfile?.avatar_url || "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file ảnh");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Kích thước ảnh không được vượt quá 5MB");
      return;
    }

    setUploading(true);
    try {
      // Convert to base64 for demo purposes
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFormData((prev) => ({ ...prev, avatar_url: base64 }));
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Có lỗi xảy ra khi tải ảnh lên");
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const email = localStorage.getItem("userEmail");

      // Update profile via Supabase Edge Function
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_SUPABASE_URL
        }/functions/v1/update-profile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
            }`,
          },
          body: JSON.stringify({
            email,
            full_name: formData.full_name,
            phone: formData.phone,
            bio: formData.bio,
            avatar_url: formData.avatar_url,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        const updatedProfile = { ...userProfile, ...formData };
        onUpdate(updatedProfile);
        alert("Cập nhật profile thành công!");
      } else {
        // Fallback: update localStorage only
        const updatedProfile = { ...userProfile, ...formData };
        onUpdate(updatedProfile);
        alert("Cập nhật profile thành công!");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // Fallback: update localStorage only
      const updatedProfile = { ...userProfile, ...formData };
      onUpdate(updatedProfile);
      alert("Cập nhật profile thành công!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Chỉnh sửa profile
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <i className="ri-user-line text-2xl text-gray-400"></i>
                )}
              </div>
              <div>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <span className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer inline-flex items-center whitespace-nowrap">
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Đang tải...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-line mr-2"></i>
                        Tải ảnh lên
                      </>
                    )}
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  JPG, PNG tối đa 5MB
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Họ và tên *
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      full_name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập họ và tên..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập email..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số điện thoại..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Giới thiệu bản thân
              </label>
              <textarea
                rows={4}
                value={formData.bio}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bio: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Viết vài dòng giới thiệu về bản thân..."
                maxLength={500}
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">
                {formData.bio.length}/500 ký tự
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Lưu thay đổi
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
